/**
 * ResetPasswordPage - Set new password with token from email
 * MODULE: core/auth/frontend/pages/ResetPasswordPage.tsx
 */
import { useState, FormEvent, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, TextInput, Label, Button, Alert, Spinner } from 'flowbite-react';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

type Status = 'loading' | 'invalid' | 'valid' | 'submitting' | 'success' | 'error';

function getStrength(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  return Math.min(score, 4);
}

export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [strength, setStrength] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/password/validate-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setStatus(data.valid ? 'valid' : 'invalid');
      } catch {
        if (!cancelled) setStatus('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || newPassword !== confirmPassword || strength < 3) return;
    setError(null);
    setStatus('submitting');
    try {
      const res = await fetch(`${API_BASE}/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Reset failed');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full mx-auto p-8 text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Validating your reset link...</p>
        </Card>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full mx-auto p-6">
          <Alert color="failure" className="mb-4">
            This reset link is invalid or has expired.
          </Alert>
          <Link to="/forgot-password">
            <Button color="blue" className="w-full">Request a new link</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full mx-auto p-6 text-center">
          <Alert color="success" className="mb-4">Password updated successfully!</Alert>
          <Link to="/login">
            <Button color="blue" className="w-full">Go to login</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const canSubmit = strength >= 3 && newPassword === confirmPassword && newPassword.length >= 8;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-md w-full mx-auto p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Brazn</h1>
          <p className="text-gray-600 text-sm mt-1">Set your new password</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>{error}</Alert>
          )}
          <div>
            <Label htmlFor="newPassword">New password</Label>
            <TextInput
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={status === 'submitting'}
              className="mt-1"
            />
            <div className="mt-2">
              <PasswordStrengthIndicator password={newPassword} onStrengthChange={setStrength} />
            </div>
          </div>
          <div>
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <TextInput
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={status === 'submitting'}
              className="mt-1"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-600 mt-1">Passwords don&apos;t match</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={!canSubmit || status === 'submitting'}>
            {status === 'submitting' ? <><Spinner size="sm" className="mr-2" /> Resetting...</> : 'Reset password'}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm text-gray-600">
          <Link to="/login" className="text-blue-600 hover:underline">Back to login</Link>
        </p>
      </Card>
    </div>
  );
}
